import { Button, Empty, List, Popconfirm, Spin, Tabs, Tag } from "@arco-design/web-react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { fetchCustomizationProjectDemandVersionOverviews } from "../repo/customizationRepo";
import type {
  CustomizationDemandVersionOption,
  CustomizationProjectListItem,
} from "../types";
import { formatBeijingCompactTime } from "./customizationProjectListTime";

type Props = {
  items: CustomizationProjectListItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onOpenWorkbench: (projectId: string) => void;
  onOpenBudget?: (budgetOrderId: string) => void;
  onOpenOrderHandover?: (
    item: CustomizationProjectListItem,
  ) => void | Promise<void>;
  onOpenItineraryPreview?: (itineraryId: string) => void;
  onDeleteProject?: (projectId: string) => Promise<void>;
  onDeleteDemand?: (
    projectId: string,
    demandId: string,
    options?: { isRootVersion: boolean },
  ) => Promise<void>;
};

/** 与商机工作台移动断点一致：窄屏只保留项目摘要，不展开版本与删除。 */
const OPPORTUNITY_PROJECT_LIST_COMPACT_MAX_WIDTH = 760;

const currencyFormatter = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
});

/** 商机抽屉内按定制项目归组展示全部需求版本。 */
export function CustomizationOpportunityProjectList({
  items,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onOpenWorkbench,
  onOpenBudget,
  onOpenOrderHandover,
  onOpenItineraryPreview,
  onDeleteProject,
  onDeleteDemand,
}: Props) {
  const isCompactSummary = useIsCompactOpportunityProjectList(
    OPPORTUNITY_PROJECT_LIST_COMPACT_MAX_WIDTH,
  );
  const [orderHandoverLoadingId, setOrderHandoverLoadingId] = useState<string | null>(null);

  /** 创订单/查订单期间按钮转圈，并避免重复点击。 */
  async function handleOpenOrderHandover(item: CustomizationProjectListItem) {
    if (!onOpenOrderHandover || orderHandoverLoadingId) {
      return;
    }
    setOrderHandoverLoadingId(item.id);
    try {
      await onOpenOrderHandover(item);
    } finally {
      setOrderHandoverLoadingId(null);
    }
  }

  return (
    <List
      className={[
        "customization-opportunity-project-list",
        isCompactSummary ? "is-compact-summary" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      dataSource={items}
      loading={loading}
      bordered={false}
      split={false}
      hoverable
      paginationInFooter
      noDataElement={<Empty description="暂无关联定制" />}
      pagination={
        total > 0
          ? {
              current: page,
              pageSize,
              total,
              sizeCanChange: !isCompactSummary,
              sizeOptions: [10, 20, 50],
              showTotal: true,
              onChange: onPageChange,
              onPageSizeChange,
            }
          : false
      }
      render={(item) => {
        const projectCode = (item.legacyId || item.projectCode || "").trim();
        const projectName = item.name?.trim() || "";
        const displayTitle = projectName || projectCode || "未命名定制";
        const showCodeSeparately = Boolean(
          projectCode && projectName && projectCode !== projectName,
        );
        const hasItinerary = projectHasItinerary(item);
        const orderActionLabel = item.isFormed ? "查订单" : "创订单";

        return (
          <List.Item key={item.id} className="customization-opportunity-project-item">
            <article
              className="customization-opportunity-project"
              aria-label={`定制项目 ${displayTitle}`}
            >
              <header className="customization-opportunity-project-header">
                <div className="customization-opportunity-project-title">
                  {item.isFormed && !isCompactSummary ? (
                    <button
                      type="button"
                      className="customization-opportunity-formed-mark is-active"
                      aria-label={`进入合同交接及人员名单 ${displayTitle}`}
                      title="已成团，进入合同交接及人员名单"
                      onClick={() => void handleOpenOrderHandover(item)}
                    >
                      $
                    </button>
                  ) : (
                    <span
                      className={`customization-opportunity-formed-mark${item.isFormed ? " is-active" : ""}`}
                      aria-label={item.isFormed ? "已成团" : "未成团"}
                      title={item.isFormed ? "已成团" : "未成团"}
                    >
                      $
                    </span>
                  )}
                  <div className="customization-opportunity-project-title-text">
                    {showCodeSeparately ? (
                      <span className="customization-opportunity-project-code">{projectCode}</span>
                    ) : null}
                    <Button
                      type="text"
                      className="customization-opportunity-project-name"
                      onClick={() => onOpenWorkbench(item.id)}
                    >
                      {displayTitle}
                    </Button>
                  </div>
                </div>
                <div className="customization-opportunity-project-actions">
                  {hasItinerary && onOpenOrderHandover && !isCompactSummary ? (
                    <Button
                      type="primary"
                      size="mini"
                      loading={orderHandoverLoadingId === item.id}
                      disabled={Boolean(orderHandoverLoadingId)}
                      className="customization-opportunity-create-order-btn"
                      aria-label={`${orderActionLabel} ${displayTitle}`}
                      onClick={() => void handleOpenOrderHandover(item)}
                    >
                      {orderActionLabel}
                    </Button>
                  ) : null}
                  <Tag size="small" color={item.isFormed ? "green" : "gray"}>
                    {item.isFormed ? "已成团" : "未成团"}
                  </Tag>
                  {!isCompactSummary && onDeleteProject ? (
                    <Popconfirm
                      title="确认删除整个定制项目？"
                      content="有行程、核价、报价、订单或履约数据时系统会自动改为逻辑归档。"
                      okText="删除"
                      cancelText="取消"
                      okButtonProps={{ status: "danger" }}
                      onOk={() => onDeleteProject(item.id)}
                    >
                      <Button
                        type="text"
                        size="mini"
                        status="danger"
                        icon={<Trash2 size={14} />}
                        aria-label={`删除定制 ${displayTitle}`}
                      />
                    </Popconfirm>
                  ) : null}
                </div>
              </header>

              <div className="customization-opportunity-project-meta">
                <ProjectMeta label="学校" value={item.organizationNameSnapshot} wrap />
                <ProjectMeta label="客户联系人" value={buildCustomerContact(item)} wrap />
                <ProjectMeta label="销售" value={item.salesUserName} tone="sales" />
                <ProjectMeta label="计调" value={item.dispatcherUserName} tone="dispatcher" />
              </div>

              {isCompactSummary ? null : (
                <DemandVersionList
                  projectId={item.id}
                  projectTitle={displayTitle}
                  demandVersionCount={item.demandVersionCount}
                  onOpenBudget={onOpenBudget}
                  onOpenItineraryPreview={onOpenItineraryPreview}
                  onDeleteDemand={onDeleteDemand}
                />
              )}
            </article>
          </List.Item>
        );
      }}
    />
  );
}

/** 展示项目级业务字段：标签与值同一行，降低卡片高度噪音。 */
function ProjectMeta({
  label,
  value,
  wrap = false,
  tone,
}: {
  label: string;
  value?: string;
  /** 长文案换行展示全文，避免单行省略看不全。 */
  wrap?: boolean;
  /** 销售/计调等角色字段的轻量色点缀。 */
  tone?: "sales" | "dispatcher";
}) {
  const text = value?.trim() || "--";
  return (
    <div
      className={[
        "customization-opportunity-project-meta-item",
        wrap ? "is-wrap" : "",
        tone ? `is-${tone}` : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span>{label}</span>
      {/* 空 title 抑制 Chromium/嵌入浏览器对省略文本自动弹出全文提示。 */}
      <strong title="">{text}</strong>
    </div>
  );
}

/** 查询并展示单个项目的需求版本；有多方案时默认展开，仅一方案时顶层直出。 */
function DemandVersionList({
  projectId,
  projectTitle,
  demandVersionCount,
  onOpenBudget,
  onOpenItineraryPreview,
  onDeleteDemand,
}: {
  projectId: string;
  projectTitle: string;
  demandVersionCount?: number;
  onOpenBudget?: (budgetOrderId: string) => void;
  onOpenItineraryPreview?: (itineraryId: string) => void;
  onDeleteDemand?: (
    projectId: string,
    demandId: string,
    options?: { isRootVersion: boolean },
  ) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState<string | undefined>();
  const autoExpandedRef = useRef(false);
  const query = useQuery({
    queryKey: ["customization", "projects", projectId, "demand-version-overviews"],
    queryFn: () => fetchCustomizationProjectDemandVersionOverviews(projectId),
  });
  const versions = query.data?.items ?? [];
  /** Tab 从 V1 向右递增，不跟随接口倒序。 */
  const versionsAscending = useMemo(
    () =>
      [...versions].sort((left, right) => {
        const seqDiff = (left.versionSeq ?? 0) - (right.versionSeq ?? 0);
        if (seqDiff !== 0) {
          return seqDiff;
        }
        return String(left.versionNumber ?? "").localeCompare(
          String(right.versionNumber ?? ""),
          "zh-CN",
          { numeric: true },
        );
      }),
    [versions],
  );
  const versionsWithPlan = useMemo(
    () => versionsAscending.filter(versionHasPlan),
    [versionsAscending],
  );
  const singlePlanVersion = versionsWithPlan.length === 1 ? versionsWithPlan[0] : undefined;
  const resolvedCount = query.data ? versionsAscending.length : demandVersionCount;
  const countLabel =
    typeof resolvedCount === "number" && Number.isFinite(resolvedCount)
      ? `共 ${resolvedCount} 个`
      : "";
  const toggleLabel = expanded ? "收起需求版本" : "展开需求版本";
  const hintLabel = expanded
    ? countLabel || (query.isLoading || query.isFetching ? "加载中…" : "")
    : countLabel || (query.isLoading || query.isFetching ? "加载中…" : "点击展开");

  useEffect(() => {
    if (autoExpandedRef.current || !query.data) {
      return;
    }
    if (versionsWithPlan.length > 1) {
      setExpanded(true);
      autoExpandedRef.current = true;
    }
  }, [query.data, versionsWithPlan.length]);

  useEffect(() => {
    if (!versionsAscending.length) {
      setActiveVersionId(undefined);
      return;
    }
    setActiveVersionId((current) => {
      if (current && versionsAscending.some((version) => version.id === current)) {
        return current;
      }
      const currentVersion = versionsAscending.find((version) => version.isCurrent);
      return currentVersion?.id ?? versionsAscending[versionsAscending.length - 1]?.id;
    });
  }, [versionsAscending]);

  return (
    <section
      className={`customization-opportunity-version-section${expanded ? " is-expanded" : ""}`}
      aria-label="需求版本"
    >
      {singlePlanVersion ? (
        <PlanLink
          className="customization-opportunity-plan-link is-top-level"
          version={singlePlanVersion}
          projectTitle={projectTitle}
          onOpenItineraryPreview={onOpenItineraryPreview}
        />
      ) : null}
      <button
        type="button"
        className="customization-opportunity-version-section-title"
        aria-expanded={expanded}
        aria-label={toggleLabel}
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="customization-opportunity-version-section-heading">
          {expanded ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
          <strong>需求版本</strong>
        </span>
        <span className="customization-opportunity-version-section-hint">{hintLabel}</span>
      </button>
      {expanded ? (
        <Spin loading={query.isLoading || query.isFetching} style={{ width: "100%" }}>
          {query.isError ? (
            <div className="customization-opportunity-version-error" role="alert">
              需求版本加载失败
            </div>
          ) : versions.length === 0 && !(query.isLoading || query.isFetching) ? (
            <Empty description="暂无需求版本" />
          ) : versions.length > 0 ? (
            <div
              className="customization-opportunity-version-tabs-shell"
              aria-label="需求版本分组"
            >
              <Tabs
                type="rounded"
                activeTab={activeVersionId}
                onChange={setActiveVersionId}
                className="customization-opportunity-version-tabs"
              >
                {versionsAscending.map((version) => {
                  const versionLabel = version.versionNumber || `V${version.versionSeq}`;
                  const hasPlan = versionHasPlan(version);
                  const titleHints = [
                    versionLabel,
                    hasPlan ? "有方案" : "无方案",
                    version.isCurrent ? "当前" : "",
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <Tabs.TabPane
                      key={version.id}
                      title={
                        <span
                          className={[
                            "customization-opportunity-version-tab-title",
                            version.isCurrent ? "is-current" : "",
                            hasPlan ? "has-plan" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          title={titleHints}
                        >
                          <strong>{versionLabel}</strong>
                          {hasPlan ? (
                            <em className="customization-opportunity-version-tab-plan">方案</em>
                          ) : null}
                          {version.isCurrent ? <i aria-hidden="true" /> : null}
                        </span>
                      }
                    >
                      <DemandVersionItem
                        version={version}
                        projectTitle={projectTitle}
                        showInlinePlan={!singlePlanVersion}
                        hideVersionTag
                        onOpenBudget={onOpenBudget}
                        onOpenItineraryPreview={onOpenItineraryPreview}
                        onDelete={
                          onDeleteDemand
                            ? () =>
                                onDeleteDemand(projectId, version.id, {
                                  isRootVersion: isRootDemandVersion(version),
                                })
                            : undefined
                        }
                      />
                    </Tabs.TabPane>
                  );
                })}
              </Tabs>
            </div>
          ) : null}
        </Spin>
      ) : null}
    </section>
  );
}

/** 展示单个需求版本的行程、核价、报价与目的地摘要。 */
function DemandVersionItem({
  version,
  projectTitle,
  showInlinePlan,
  hideVersionTag = false,
  onOpenBudget,
  onOpenItineraryPreview,
  onDelete,
}: {
  version: CustomizationDemandVersionOption;
  projectTitle: string;
  showInlinePlan: boolean;
  /** Tab 标题已展示版本号时隐藏卡片内重复标签。 */
  hideVersionTag?: boolean;
  onOpenBudget?: (budgetOrderId: string) => void;
  onOpenItineraryPreview?: (itineraryId: string) => void;
  onDelete?: () => Promise<void>;
}) {
  const versionLabel = version.versionNumber || `V${version.versionSeq}`;
  const isRootVersion = isRootDemandVersion(version);
  const finalizedBudgetCount = version.finalizedBudgetCount ?? 0;
  const budgetClickable = Boolean(
    finalizedBudgetCount > 0 && version.latestFinalizedBudgetOrderId,
  );
  const destinationText = formatDestinationSummary(version);

  return (
    <article
      className={`customization-opportunity-version-item${version.isCurrent ? " is-current" : ""}`}
      aria-label={`需求版本 ${versionLabel}`}
    >
      <header>
        <div className="customization-opportunity-version-item-tags">
          {hideVersionTag ? null : (
            <Tag color="arcoblue" size="small">
              {versionLabel}
            </Tag>
          )}
          {version.isCurrent && !hideVersionTag ? (
            <Tag color="green" size="small">
              当前
            </Tag>
          ) : null}
          <time>{formatBeijingCompactTime(version.updatedAt || version.createdAt)}</time>
        </div>
        {onDelete ? (
          <Popconfirm
            title={isRootVersion ? "确认删除 V1？" : "确认删除此需求版本？"}
            content={
              isRootVersion
                ? "V1 不能单独删除。确认后将删除该定制项目，并停用整个商机。"
                : "存在下级业务关联或被后续版本引用时将逻辑归档。"
            }
            okText={isRootVersion ? "删除并停用商机" : "删除"}
            cancelText="取消"
            okButtonProps={{ status: "danger" }}
            onOk={onDelete}
          >
            <Button
              type="text"
              size="mini"
              status="danger"
              icon={<Trash2 size={13} />}
              aria-label={`删除需求版本 ${versionLabel}`}
            />
          </Popconfirm>
        ) : null}
      </header>

      {showInlinePlan && versionHasPlan(version) ? (
        <PlanLink
          version={version}
          projectTitle={projectTitle}
          hideVersionPrefix
          onOpenItineraryPreview={onOpenItineraryPreview}
        />
      ) : null}

      <div className="customization-opportunity-version-facts">
        <VersionFact label="出发" value={version.departureStartDate || "--"} />
        <VersionFact label="人数" value={formatPeople(version)} />
        <VersionFact label="行程" value={String(version.itineraryCount ?? 0)} />
        <div className="customization-opportunity-version-fact">
          <span>核价</span>
          {budgetClickable ? (
            <Button
              type="text"
              size="small"
              aria-label={`打开 ${versionLabel} 最新核价，共 ${finalizedBudgetCount} 个`}
              onClick={() => onOpenBudget?.(version.latestFinalizedBudgetOrderId as string)}
            >
              {finalizedBudgetCount}
            </Button>
          ) : (
            <strong>{finalizedBudgetCount}</strong>
          )}
        </div>
        <VersionFact
          label="报价"
          value={formatCurrency(version.latestFinalizedQuoteTotalAmount)}
          emphasized
        />
      </div>

      {destinationText ? (
        <p className="customization-opportunity-destination-line" title={destinationText}>
          {destinationText}
        </p>
      ) : null}
    </article>
  );
}

/** 可点击的方案/行程入口；有行程标题时始终展示。 */
function PlanLink({
  version,
  className,
  hideVersionPrefix = false,
  onOpenItineraryPreview,
}: {
  version: CustomizationDemandVersionOption;
  className?: string;
  projectTitle?: string;
  /** 版本卡内已有 V 标签时隐藏标题前缀，多留字给行程名。 */
  hideVersionPrefix?: boolean;
  onOpenItineraryPreview?: (itineraryId: string) => void;
}) {
  const versionLabel = version.versionNumber || `V${version.versionSeq}`;
  const itineraryTitle = version.itineraryTitle?.trim() || "";
  const latestItineraryId = version.latestItineraryId?.trim() || "";
  const clickable = Boolean(latestItineraryId && onOpenItineraryPreview);
  const displayTitle = buildPlanDisplayTitle(versionLabel, itineraryTitle, hideVersionPrefix);
  const ariaTitle = itineraryTitle || displayTitle;

  if (clickable) {
    return (
      <button
        type="button"
        className={className ?? "customization-opportunity-plan-link"}
        aria-label={`打开行程 ${ariaTitle}`}
        title={ariaTitle}
        onClick={() => onOpenItineraryPreview?.(latestItineraryId)}
      >
        <span className="customization-opportunity-plan-link-label">方案</span>
        <strong>{displayTitle}</strong>
        <span className="customization-opportunity-plan-link-action">查看</span>
      </button>
    );
  }

  return (
    <div
      className={className ?? "customization-opportunity-plan-link is-static"}
      aria-label="方案"
      title={ariaTitle}
    >
      <span className="customization-opportunity-plan-link-label">方案</span>
      <strong>{displayTitle}</strong>
    </div>
  );
}

/** 组装方案展示文案；有行程标题时必须带出，截断交给样式省略。 */
function buildPlanDisplayTitle(
  versionLabel: string,
  itineraryTitle: string,
  hideVersionPrefix = false,
): string {
  if (!itineraryTitle) {
    return hideVersionPrefix ? "查看方案" : `${versionLabel} · 查看方案`;
  }
  if (hideVersionPrefix) {
    return itineraryTitle;
  }
  return `${versionLabel} · ${itineraryTitle}`;
}

/** 判断定制项目是否已有行程，用于关联定制创订单入口展示。 */
function projectHasItinerary(item: CustomizationProjectListItem): boolean {
  return Boolean(item.hasItinerary) || (item.itineraryCount ?? 0) > 0;
}

/** 判断是否为首版 V1（不可单独删除）。 */
function isRootDemandVersion(version: CustomizationDemandVersionOption): boolean {
  return version.versionSeq === 1 || version.versionNumber === "V1";
}

/** 判断需求版本是否已有方案/行程。 */
function versionHasPlan(version: CustomizationDemandVersionOption): boolean {
  return Boolean(
    version.hasItinerary ||
      (version.itineraryCount ?? 0) > 0 ||
      version.latestItineraryId?.trim(),
  );
}

/** 目的地摘要压成一行，去掉空字段套盒。 */
function formatDestinationSummary(version: CustomizationDemandVersionOption): string {
  const parts = [
    version.provinceNames?.length ? `省份 ${version.provinceNames.join("、")}` : "",
    version.stayAreaNames?.length ? `住宿 ${version.stayAreaNames.join("、")}` : "",
    version.pointNames?.length ? `考察 ${version.pointNames.join("、")}` : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

/** 展示需求版本的单个业务事实。 */
function VersionFact({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div className="customization-opportunity-version-fact">
      <span>{label}</span>
      <strong className={emphasized ? "is-emphasized" : undefined}>{value}</strong>
    </div>
  );
}

/** 组合项目客户与联系人名称。 */
function buildCustomerContact(item: CustomizationProjectListItem): string {
  const values = [item.customerName, item.contactName]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  return values.join(" / ") || "--";
}

/** 格式化需求版本师生人数。 */
function formatPeople(version: CustomizationDemandVersionOption): string {
  if (version.studentCount == null && version.teacherCount == null) {
    return "--";
  }
  return `${version.studentCount ?? 0}学生 + ${version.teacherCount ?? 0}老师`;
}

/** 格式化人民币金额。 */
function formatCurrency(value?: string): string {
  if (!value) {
    return "--";
  }
  const amount = Number(value);
  return Number.isFinite(amount) ? currencyFormatter.format(amount) : "--";
}

/** 窄屏摘要模式：隐藏需求版本区与项目删除。 */
function useIsCompactOpportunityProjectList(maxWidth: number) {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setIsCompact(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener?.("change", sync);
    return () => mediaQuery.removeEventListener?.("change", sync);
  }, [maxWidth]);

  return isCompact;
}
